import React, { useEffect } from 'react';
import { Box, Grid, Tab, Tabs, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';

import TabPanel from './TabPanel';
import { colorPalette, TCard } from '../../myde-react-components';
import { fontStyle } from '../../../styles/style';
import { ClickHandlerType } from '../../../types/commonTypes';
import { useTheme } from '../../../providers/custom-theme-provider';

export interface CustomTabProps {
  tabList: TabListType[];
  isVertical?: boolean;
  borderRequired: boolean;
  selectedTab?: number;
  tabSelected?: ClickHandlerType;
  showBottomBorder: boolean;
  extraContent?: any;
}

export interface TabListType {
  label: string;
  component: JSX.Element;
  id?: any;
  tabChild?: any;
  icon?: JSX.Element;
}

interface StyleProps {
  borderRequired: boolean;
  isVertical: boolean;
  currentThemes: Theme;
  showBottomBorder: boolean;
}

const useStyles = makeStyles<Theme, StyleProps>({
  cards: {
    minHeight: 500,
    marginTop: 20,
    borderRadius: ({ borderRequired }) => (borderRequired ? 10 : 0),
    border: ({ borderRequired }) => (borderRequired ? `1px solid ${colorPalette.border.card}` : 'none'),
  },
  tabListWrapper: {
    borderRight: ({ isVertical }) => (isVertical ? `1px solid ${colorPalette.border.card}` : 'none'),
    borderBottom: ({ isVertical, showBottomBorder }) =>
      !isVertical && showBottomBorder ? `1px solid ${colorPalette.border.card}` : 'none',
    height: ({ isVertical }) => (isVertical ? '100%' : 'auto'),
    '& .MuiTabs-indicator': {
      display: ({ isVertical }) => (isVertical ? 'none' : 'inherit'),
      backgroundColor: ({ currentThemes }) => `${currentThemes.palette.primary.main} !important`,
    },
  },
  tabItemStyle: {
    borderTop: ({ borderRequired }) =>
      borderRequired ? `1px solid ${colorPalette.border.card} !important` : 'none !important',
    justifyContent: 'flex-start !important',
    paddingLeft: '10px !important',
    alignItems: 'start !important',
    textAlign: 'left !important' as any,
    maxWidth: '100% !important',
    color: `${colorPalette.typoText.darken2}`,
    textTransform: 'unset !important' as any,
    '&:first-child': {
      borderTop: 'none !important',
      '&.Mui-selected': {
        borderRight: 'none',
        color: ({ currentThemes }) => `${currentThemes.palette.primary.main} !important`,
      },
    },
    '&:nth-child(2)': {
      borderTop: 'none !important',
    },
    '& .MuiTab-root': {
      padding: '10px 16px',
    },
    '&.Mui-selected': {
      color: ({ currentThemes }) => `${currentThemes.palette.primary.main} !important`,
      fontWeight: ({ isVertical }) => (isVertical ? `${fontStyle.fontWeight.font700.fontWeight}` : 'inherit'),
    },
  },
  tabLabelWrapper: {
    position: 'relative',
    textAlign: ({ isVertical }) => (!isVertical ? 'center' : 'left'),
  },
  tabItemLabel: {
    verticalAlign: 'super',
    marginLeft: 5,
  },
  rightIcon: {
    position: 'absolute',
    top: '50%',
    right: 0,
    transform: 'translateY(-50%)',
  },
});

const basicTabProps = (index: number) => {
  return {
    id: `custom-tab-${index}`,
    'aria-controls': `custom-tabpanel-${index}`,
  };
};

const CustomTabs = ({
  tabList,
  isVertical = false,
  borderRequired = false,
  selectedTab,
  showBottomBorder,
  tabSelected,
  extraContent = <></>,
}: CustomTabProps) => {
  const { currentThemes } = useTheme();
  const styleProps: StyleProps = {
    borderRequired: borderRequired,
    isVertical: isVertical,
    currentThemes: currentThemes,
    showBottomBorder,
  };
  const classes = useStyles(styleProps);
  const [value, setValue] = React.useState(selectedTab || 0);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    const currentTab = tabList[newValue];
    setValue(currentTab?.id || newValue);
  };

  useEffect(() => {
    if (!selectedTab && tabList && tabList.length > 0) {
      setValue(tabList[0]?.id || 0);
    }
  }, [tabList]);

  useEffect(() => {
    if (tabSelected) {
      tabSelected(value);
    }
  }, [value]);

  return (
    <>
      <TCard className={classes.cards} elevation={borderRequired ? 1 : 0}>
        <Grid container direction="row" sx={{ m: 0 }}>
          <Grid item xs={12} sm={12} md={isVertical ? 3 : 12} lg={isVertical ? 4 : 12}>
            <Box sx={{ p: isVertical ? 2 : 0 }} className={classes.tabListWrapper}>
              <Box> {extraContent}</Box>
              <Tabs
                value={value}
                sx={{ bgcolor: 'background.paper' }}
                onChange={handleChange}
                orientation={isVertical ? 'vertical' : 'horizontal'}
                variant="scrollable"
              >
                {tabList?.map((tabItem, index) => (
                  <Tab
                    className={classes.tabItemStyle}
                    key={index}
                    label={
                      !tabItem.tabChild ? (
                        <Grid
                          container
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          className={classes.tabLabelWrapper}
                        >
                          <Grid item xs={12} sm={12} md={12} lg={12}>
                            <Box sx={{ py: 1 }} className="flex-basic-start">
                              {tabItem.icon && <Box className="iconTab">{tabItem.icon}</Box>}
                              <Box
                                className={clsx({
                                  [classes.tabItemLabel]: isVertical,
                                  ['tabText']: true,
                                })}
                              >
                                {tabItem.label}
                              </Box>
                            </Box>
                          </Grid>
                          {isVertical && (
                            <Grid item className={classes.rightIcon}>
                              <Box
                                className="icon-arrow-right textColor-200 actionIconFont-24"
                                sx={{ float: 'right', mt: 1 }}
                              />
                            </Grid>
                          )}
                        </Grid>
                      ) : (
                        tabItem.tabChild
                      )
                    }
                    value={tabItem?.id || index}
                    {...basicTabProps(tabItem?.id || index)}
                  />
                ))}
              </Tabs>
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={isVertical ? 9 : 12} lg={isVertical ? 8 : 12}>
            <Box>
              {tabList?.map((tabItem, idx) => (
                <TabPanel key={idx} value={value} index={tabItem?.id || idx}>
                  {tabItem.component}
                </TabPanel>
              ))}
            </Box>
          </Grid>
        </Grid>
      </TCard>
    </>
  );
};

export default CustomTabs;
